import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import type FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import type { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";

export type TemplateCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

/** What the reader asked for: these categories, added to the schedule's or in place of them. */
export type TemplateCategoriesApplied = {
  categories: TemplateCategory[];
  mode: "add" | "replace";
};

const Root = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
}));
const Header = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));
const CategoryList = styled(List)(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
}));
const Description = styled("span")(() => ({
  display: "-webkit-box",
  overflow: "hidden",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
const Footer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

type FeeScheduleTemplateCategoryPickerProps = {
  template: FeeScheduleTemplate;
  /** What the schedule being built already holds, so a category it has is not added twice. */
  existingCategories: readonly TemplateCategory[];
  onBack: () => void;
  onApply: (applied: TemplateCategoriesApplied) => void;
};

/**
 * One template's categories, to look over and pick from before any is added (#6604).
 *
 * All of them start ticked, except one the schedule already has — by name, as the schedule tells
 * categories apart, or by id — which is marked "Already added" and left unticked, so taking the rest
 * makes no duplicates. The footer says what will happen in so many categories, and it is where the
 * reader adds them or replaces the schedule's with them, rather than in a dialog asked afterwards.
 */
export default function FeeScheduleTemplateCategoryPicker(props: Readonly<FeeScheduleTemplateCategoryPickerProps>) {
  const { template, existingCategories, onBack, onApply } = props;
  const categories = template.categories;
  const isAlreadyAdded = (category: TemplateCategory) =>
    existingCategories.some(
      (existing) =>
        canonicalName(existing) === canonicalName(category) ||
        (Boolean(existing.id?.value) && existing.id?.value === category.id?.value),
    );
  const [ticked, setTicked] = useState<ReadonlySet<string>>(
    () => new Set(categories.filter((category) => !isAlreadyAdded(category)).map(keyOf)),
  );

  const chosen = categories.filter((category) => ticked.has(keyOf(category)));
  const canReplace = existingCategories.length > 0;

  function toggle(category: TemplateCategory) {
    const key = keyOf(category);
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <Root data-testid="template-category-picker">
      <Header>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <span>
          <Button size="small" onClick={() => setTicked(new Set(categories.map(keyOf)))}>
            Select all
          </Button>
          <Button size="small" onClick={() => setTicked(new Set())}>
            Select none
          </Button>
        </span>
      </Header>
      <Typography variant="subtitle1" component="h3" sx={{ mt: 1 }}>
        {template.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {`${chosen.length} of ${categories.length} ${categories.length === 1 ? "category" : "categories"} chosen`}
      </Typography>
      <CategoryList aria-label={`Categories in ${template.name}`}>
        {categories.length === 0 && (
          <ListItem>
            <ListItemText primary="This template has no categories" />
          </ListItem>
        )}
        {categories.map((category) => {
          const key = keyOf(category);
          const labelId = `template-category-${key}`;
          const alreadyAdded = isAlreadyAdded(category);
          return (
            <ListItem key={key} disablePadding>
              <ListItemButton dense onClick={() => toggle(category)}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={ticked.has(key)}
                    tabIndex={-1}
                    disableRipple
                    slotProps={{ input: { "aria-labelledby": labelId } }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={
                    <>
                      {category.name.value}
                      {alreadyAdded && <Chip label="Already added" size="small" sx={{ ml: 1 }} />}
                    </>
                  }
                  secondary={
                    <>
                      <span>{`${kindOf(category)}${category.feeString}`}</span>
                      {category.description?.value && <Description>{category.description.value}</Description>}
                    </>
                  }
                  slotProps={{ secondary: { component: "div" } }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </CategoryList>
      <Footer>
        <Button
          variant="contained"
          disabled={chosen.length === 0}
          onClick={() => onApply({ categories: chosen, mode: "add" })}
        >
          {`Add ${chosen.length} ${chosen.length === 1 ? "category" : "categories"}`}
        </Button>
        {canReplace && (
          <Button
            variant="outlined"
            disabled={chosen.length === 0}
            onClick={() => onApply({ categories: chosen, mode: "replace" })}
          >
            {`Replace with ${chosen.length}`}
          </Button>
        )}
      </Footer>
    </Root>
  );
}

function canonicalName(category: TemplateCategory): string {
  return category.name.value.trim().toLowerCase();
}

/** A category's place in the picker. Its name is unique within a template, which is what the schedule keys on. */
function keyOf(category: TemplateCategory): string {
  return category.id?.value ?? canonicalName(category);
}

/**
 * "Fixed" as the schedule's own Fixed? column means it — a fee with no unit — and "Hourly" for an hourly
 * rate. A fee per anything else says so in its fee ("/ filing"), and needs no word of its own.
 */
function kindOf(category: TemplateCategory): string {
  if (!(category instanceof FeeScheduleCategory)) return "";
  if (category.isHourly) return "Hourly · ";
  return category.fee?.unit ? "" : "Fixed · ";
}
